import { useRef, useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../hooks/useAuth.js';
import { NotificationBell } from './NotificationCenter.js';

export function UserMenu() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  if (!user) return null;

  function handleLogout() {
    setOpen(false);
    logout();
  }

  function handleCopyId() {
    void navigator.clipboard.writeText(user!.id);
    toast.success('User ID copied');
    setOpen(false);
  }

  return (
    <div className="px-3 py-3" style={{ borderTop: '1px solid var(--glass-border)' }}>
      {/* Notification bell + user button row */}
      <div className="flex items-center gap-2 mb-1.5">
        <NotificationBell />
        <div className="flex-1 min-w-0" ref={menuRef}>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            title="Account menu"
            className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-xs transition-all duration-150"
            style={{
              background: open ? 'rgba(13,207,184,0.12)' : 'var(--bg-tertiary)',
              border: open ? '1px solid rgba(13,207,184,0.35)' : '1px solid var(--border)',
            }}
          >
            <div
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white shadow"
              style={{
                background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)',
                boxShadow: '0 2px 8px rgba(13,207,184,0.35)',
              }}
            >
              {user.name[0]?.toUpperCase()}
            </div>
            <div className="min-w-0 text-left flex-1">
              <div className="truncate text-sm font-semibold text-text-primary leading-tight">{user.name}</div>
              <div className="truncate text-[11px] text-text-dimmed leading-tight capitalize">{user.role}</div>
            </div>
            <span className="text-[10px] shrink-0" style={{ color: 'var(--text-dimmed)' }}>
              {open ? '▲' : '▼'}
            </span>
          </button>

          {/* Dropdown */}
          {open && (
            <div
              className="absolute bottom-[72px] left-3 right-3 z-50 rounded-2xl overflow-hidden shadow-2xl"
              style={{
                background: 'rgba(10,42,50,0.97)',
                border: '1px solid var(--glass-border)',
                backdropFilter: 'blur(18px)',
              }}
            >
              {/* Profile header */}
              <div className="px-4 py-4" style={{ borderBottom: '1px solid var(--border)' }}>
                <div className="flex items-center gap-3">
                  <div
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-base font-bold text-white"
                    style={{ background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)' }}
                  >
                    {user.name[0]?.toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-text-primary truncate">{user.name}</div>
                    <div className="text-[11px] text-text-dimmed truncate">{user.email}</div>
                    <span
                      className="inline-flex mt-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase"
                      style={{
                        background: user.role === 'admin' ? 'rgba(6,182,212,0.18)' : 'rgba(13,207,184,0.12)',
                        color: user.role === 'admin' ? '#06b6d4' : 'var(--accent-blue)',
                        border: `1px solid ${user.role === 'admin' ? '#06b6d433' : 'rgba(13,207,184,0.25)'}`,
                      }}
                    >
                      {user.role}
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="p-2 space-y-0.5">
                <MenuRow icon="⎘" label="Copy user ID" onClick={handleCopyId} />
                <MenuRow
                  icon="⚙"
                  label="Account settings"
                  onClick={() => { setOpen(false); window.location.href = '/settings'; }}
                />
                <div className="my-1" style={{ borderTop: '1px solid var(--border)' }} />
                <MenuRow icon="⎋" label="Sign out" onClick={handleLogout} destructive />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MenuRow({
  icon,
  label,
  onClick,
  destructive,
}: {
  icon: string;
  label: string;
  onClick: () => void;
  destructive?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm transition-colors duration-100"
      style={{ color: destructive ? 'var(--accent-red)' : 'var(--text-secondary)' }}
      onMouseEnter={(e) => (e.currentTarget.style.background = destructive ? 'rgba(238,93,80,0.10)' : 'rgba(13,207,184,0.10)')}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
    >
      <span className="w-4 text-center text-base">{icon}</span>
      {label}
    </button>
  );
}
